import { useState, useEffect, createContext, useContext, useCallback, useRef, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

import { ErrorBoundary } from "@/components/ErrorBoundary";

// ── Eagerly loaded (critical path — landing, auth, shell) ──
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import AuthCallback from "@/pages/AuthCallback";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import "./styles/legal.css";

// ── Lazy loaded (deferred until route is visited) ──
const HomeV3 = lazy(() => import("@/pages/HomeV3"));
const MealPlan = lazy(() => import("@/pages/MealPlan"));
const FoodLog = lazy(() => import("@/pages/FoodLog"));
const Recipes = lazy(() => import("@/pages/Recipes"));
const GroceryList = lazy(() => import("@/pages/GroceryList"));
const Settings = lazy(() => import("@/pages/Settings"));
const UpgradePage = lazy(() => import("@/pages/UpgradePage"));
const ActivityPage = lazy(() => import("@/pages/Activity"));
const MealDelivery = lazy(() => import("@/pages/MealDelivery"));
const SnapMeal = lazy(() => import("@/pages/SnapMeal"));
const SharedMeal = lazy(() => import("@/pages/SharedMeal"));
const SharedProgress = lazy(() => import("@/pages/SharedProgress"));
const HybridPlanner = lazy(() => import("@/pages/HybridPlanner"));
const OnboardingWizard = lazy(() => import("@/pages/OnboardingWizard"));
const OnboardingFlowV3 = lazy(() => import("@/pages/OnboardingFlowV3"));
const TopMeals = lazy(() => import("@/pages/TopMeals"));
const AcceptanceCriteria = lazy(() => import("@/pages/AcceptanceCriteria"));
const AdjustMacros = lazy(() => import("@/pages/AdjustMacros"));

// Contexts
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { FeatureGateProvider } from "@/contexts/FeatureGateContext";
import { DevProOverrideProvider } from "@/contexts/DevProOverrideContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { NutritionProvider } from "@/contexts/NutritionContext";
import { FitModalProvider } from "@/components/FitModal";
import { CommandPalette } from "@/components/CommandPalette";
import { API, BACKEND_URL, apiClient } from "@/lib/api";
import { AuthContext, useAuth } from "@/contexts/AuthContext";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [debugLog, setDebugLog] = useState([]);
  const debugRef = useRef([]);

  const addDebug = useCallback((msg) => {
    const entry = `${new Date().toLocaleTimeString()}: ${msg}`;
    debugRef.current = [...debugRef.current.slice(-19), entry];
    setDebugLog([...debugRef.current]);
  }, []);

  const checkAuth = useCallback(async () => {
    const hasPendingSession = sessionStorage.getItem('vora_pending_google_session');
    if (window.location.hash?.includes('session_id=') || window.location.search?.includes('session_id=') || hasPendingSession) {
      addDebug("checkAuth: skipped (OAuth callback)");
      setLoading(false);
      return;
    }

    try {
      addDebug("checkAuth: calling /auth/me (cookie)...");
      const response = await apiClient.get("/auth/me");
      addDebug(`checkAuth: /auth/me -> ${response.status}, user=${response.data?.email}`);
      setUser(response.data);
    } catch (error) {
      addDebug(`checkAuth: /auth/me FAILED -> ${error.response?.status || error.message}`);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [addDebug]);

  useEffect(() => {
    addDebug(`mount: inIframe=${window.self !== window.top}, origin=${window.location.origin}`);
    checkAuth();
  }, [checkAuth, addDebug]);

  // ── Embedded preview auth handoff (cookie-based) ──
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "vora_auth_success") {
        addDebug("EVENT postMessage: vora_auth_success received");
        checkAuth();
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible" && !user) {
        addDebug("visibilitychange: tab visible, re-checking auth");
        checkAuth();
      }
    };

    window.addEventListener("message", onMessage);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("message", onMessage);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [checkAuth, user, addDebug]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      // silent
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, debugLog }}>
      {children}
      {/* Temporary auth debug panel — visible in preview for diagnosis */}
      {debugLog.length > 0 && (
        <div id="auth-debug-panel" style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 99999,
          background: "rgba(0,0,0,0.9)", color: "#0f0", fontSize: "10px",
          fontFamily: "monospace", padding: "8px", maxHeight: "180px",
          overflowY: "auto", borderTop: "1px solid #333",
          display: "none" // Hidden by default — open via console: document.getElementById('auth-debug-panel').style.display='block'
        }}>
          <div style={{color: "#ff0", marginBottom: 4}}>
            AUTH DEBUG | user={user ? user.email : "null"} | cookie-auth | inIframe={String(window.self !== window.top)} | route={window.location.pathname}
          </div>
          {debugLog.map((l, i) => <div key={i} style={{opacity: i === debugLog.length - 1 ? 1 : 0.6}}>{l}</div>)}
        </div>
      )}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#2D4F1E]/20"></div>
          <p className="text-[#57534E]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ── Part 7: Consent + onboarding gating ──
  // Order matters here. The route is allowed to render iff every gate
  // upstream is satisfied. Each gate redirects to /onboarding (the only
  // place these gaps can be filled) so even a deep-link cannot bypass.
  //
  //   1. signup-time legal acceptance must exist on the user record.
  //      OAuth users (Google) won't have this until they finish the new
  //      onboarding legal step, so we send them there.
  //   2. onboarding legal step (in-product) must have been completed.
  //   3. the rest of OnboardingFlowV3 (goal/body/activity/intensity)
  //      must be done — represented by `onboarding_completed`.
  //
  // The /onboarding route itself is exempt from these redirects so the
  // user has somewhere to land while they're being collected.
  const onOnboarding = location.pathname === "/onboarding";
  const needsSignupLegal = !user.legal_accepted_at;
  const needsOnboardingLegal = !user.onboarding_legal_accepted_at;
  const needsOnboardingComplete = !user.onboarding_completed;

  if (!onOnboarding && (needsSignupLegal || needsOnboardingLegal || needsOnboardingComplete)) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// ── Branded loading shell (shown while lazy chunks load) ──
const RouteLoader = () => (
  <div className="route-loader" data-testid="route-loader">
    <div className="route-loader-bar" />
  </div>
);

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check for OAuth callback: session_id in hash, query, OR pending in sessionStorage
  const hasPendingSession = typeof window !== 'undefined' && sessionStorage.getItem('vora_pending_google_session');
  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=') || hasPendingSession) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteLoader />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingFlowV3 />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <HomeV3 />
        </ProtectedRoute>
      } />
      <Route path="/meal-plan" element={
        <ProtectedRoute>
          <MealPlan />
        </ProtectedRoute>
      } />
      <Route path="/food-log" element={
        <ProtectedRoute>
          <FoodLog />
        </ProtectedRoute>
      } />
      <Route path="/recipes" element={
        <ProtectedRoute>
          <Recipes />
        </ProtectedRoute>
      } />
      <Route path="/grocery-list" element={
        <ProtectedRoute>
          <GroceryList />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/adjust-macros" element={
        <ProtectedRoute>
          <AdjustMacros />
        </ProtectedRoute>
      } />
      <Route path="/activity" element={
        <ProtectedRoute>
          <ActivityPage />
        </ProtectedRoute>
      } />
      <Route path="/meal-delivery" element={
        <ProtectedRoute>
          <MealDelivery />
        </ProtectedRoute>
      } />
      <Route path="/snap-meal" element={
        <ProtectedRoute>
          <SnapMeal />
        </ProtectedRoute>
      } />
      <Route path="/upgrade" element={
        <ProtectedRoute>
          <UpgradePage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <UpgradePage />
        </ProtectedRoute>
      } />
      <Route path="/dev/acceptance" element={
        <ProtectedRoute>
          <AcceptanceCriteria />
        </ProtectedRoute>
      } />
      <Route path="/shared-meal/:code" element={<SharedMeal />} />
      <Route path="/shared/:code" element={<SharedProgress />} />
      <Route path="/discover" element={<Navigate to="/recipes" replace />} />
      <Route path="/hybrid-planner" element={
        <ProtectedRoute>
          <HybridPlanner />
        </ProtectedRoute>
      } />
      {/* /onboarding-v2 (orphan public route) removed in Part 9 — the only
          supported onboarding entrypoint is the protected /onboarding flow. */}
      <Route path="/top-meals" element={
        <ProtectedRoute>
          <TopMeals />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <NutritionProvider>
          <FitModalProvider>
          <DevProOverrideProvider>
            <SubscriptionProvider>
            <FeatureGateProvider>
              <Toaster position="top-center" richColors />
                <div className="app-canvas">
                  <CommandPalette />
                  <AppRouter />
                </div>
            </FeatureGateProvider>
            </SubscriptionProvider>
          </DevProOverrideProvider>
          </FitModalProvider>
          </NutritionProvider>
        </AuthProvider>
      </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
export { useAuth } from "@/contexts/AuthContext";
